import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  ArmStock,
  ChassisStock,
  GetArmStocks,
  GetChassisStocks,
  GetTankStocks,
  TankStock,
  TransferArmStock,
  TransferChassisStock,
  TransferTankStock,
  UpsertArmStock,
  UpsertChassisStock,
  UpsertTankStock,
} from "../services/StockService";

const { Option } = Select;
const { TabPane } = Tabs;

type ConfigItem = { type: string; text: string };
type TransferStock =
  | { stockType: "chassis"; record: ChassisStock }
  | { stockType: "arm"; record: ArmStock }
  | { stockType: "tank"; record: TankStock };

function StockScreen() {
  const configFile = JSON.parse(localStorage.getItem("config") ?? "{}");
  const [isFetching, setIsFetching] = useState(false);

  const [chassisStocks, setChassisStocks] = useState<ChassisStock[]>([]);
  const [armStocks, setArmStocks] = useState<ArmStock[]>([]);
  const [tankStocks, setTankStocks] = useState<TankStock[]>([]);

  const [editingChassis, setEditingChassis] = useState<ChassisStock | null>(null);
  const [editingArm, setEditingArm] = useState<ArmStock | null>(null);
  const [editingTank, setEditingTank] = useState<TankStock | null>(null);
  const [chassisModalVisible, setChassisModalVisible] = useState(false);
  const [armModalVisible, setArmModalVisible] = useState(false);
  const [tankModalVisible, setTankModalVisible] = useState(false);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [transferStock, setTransferStock] = useState<TransferStock | null>(null);
  const [activeStockTab, setActiveStockTab] = useState("chassis");

  const [chassisForm] = Form.useForm();
  const [armForm] = Form.useForm();
  const [tankForm] = Form.useForm();
  const [transferForm] = Form.useForm();

  const caldronColors: ConfigItem[] = useMemo(
    () =>
      configFile?.caldron_color?.length > 0
        ? configFile.caldron_color
        : configFile?.chassis_color || [],
    [configFile]
  );

  const textMaps = useMemo(() => {
    const toMap = (list: ConfigItem[] = []) =>
      list.reduce((acc: Record<string, string>, item: ConfigItem) => {
        acc[item.type] = item.text;
        return acc;
      }, {});

    return {
      brand: toMap(configFile?.brand),
      type: toMap(configFile?.type),
      tank_size: toMap(configFile?.tank_size),
      cooler_type: toMap(configFile?.cooler_type),
      arm_type: toMap(configFile?.arm_type),
      caldron_color: toMap(caldronColors),
      caldron_type: toMap(configFile?.caldron_type),
      meme_type: toMap(configFile?.meme_type),
      stock_location: toMap(configFile?.stock_location),
    };
  }, [configFile, caldronColors]);

  const stockLocations: ConfigItem[] =
    configFile?.stock_location?.length > 0
      ? configFile.stock_location
      : [{ type: "depo", text: "Depo" }];

  const getText = (map: Record<string, string>, value?: string | number | null) => {
    if (value === undefined || value === null || value === "") return "-";
    const key = String(value);
    return map[key] || key;
  };

  const getProductionUnitText = (value?: string | null) => {
    if (value === "turbo") return "Turbo";
    if (value === "kollu") return "Kollu";
    return value || "-";
  };

  const normalizeSearchValue = (value?: string | number | null) =>
    String(value ?? "").toLocaleLowerCase("tr-TR");

  const compareValues = (left?: string | number | null, right?: string | number | null) =>
    normalizeSearchValue(left).localeCompare(normalizeSearchValue(right), "tr-TR");

  const getSearchColumnProps = <T,>(
    title: string,
    getValue: (record: T) => string | number | null | undefined
  ) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`${title} ara`}
          value={selectedKeys[0]}
          onChange={(event) =>
            setSelectedKeys(event.target.value ? [event.target.value] : [])
          }
          onPressEnter={() => confirm()}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button type="primary" size="small" onClick={() => confirm()}>
            Ara
          </Button>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.();
              confirm();
            }}
          >
            Temizle
          </Button>
        </Space>
      </div>
    ),
    onFilter: (value: string | number | boolean, record: T) =>
      normalizeSearchValue(getValue(record)).includes(normalizeSearchValue(String(value))),
    sorter: (left: T, right: T) => compareValues(getValue(left), getValue(right)),
  });

  const getNumberColumnProps = <T,>(
    title: string,
    getValue: (record: T) => number | null | undefined
  ) => ({
    ...getSearchColumnProps(title, getValue),
    sorter: (left: T, right: T) => Number(getValue(left) || 0) - Number(getValue(right) || 0),
  });

  const loadStocks = async () => {
    setIsFetching(true);
    const [chassis, arm, tank] = await Promise.all([
      GetChassisStocks(false),
      GetArmStocks(false),
      GetTankStocks(false),
    ]);

    setChassisStocks(chassis);
    setArmStocks(arm);
    setTankStocks(tank);
    setIsFetching(false);
  };

  useEffect(() => {
    loadStocks();
  }, []);

  const resetChassisForm = () => {
    setEditingChassis(null);
    chassisForm.resetFields();
    chassisForm.setFieldsValue({
      production_unit: "turbo",
      location: stockLocations[0]?.type || "depo",
      quantity: 1,
    });
  };

  const resetArmForm = () => {
    setEditingArm(null);
    armForm.resetFields();
    armForm.setFieldsValue({ location: stockLocations[0]?.type || "depo", quantity: 1 });
  };

  const resetTankForm = () => {
    setEditingTank(null);
    tankForm.resetFields();
    tankForm.setFieldsValue({ location: stockLocations[0]?.type || "depo", quantity: 1 });
  };

  useEffect(() => {
    resetChassisForm();
    resetArmForm();
    resetTankForm();
  }, []);

  const onSubmitChassis = async (values: any) => {
    const ok = await UpsertChassisStock({
      id: editingChassis?.id,
      production_unit: values.production_unit,
      type: values.type,
      brand: values.brand,
      location: values.location,
      tank_size: Number(values.tank_size),
      fan_type: values.production_unit === "turbo" ? values.fan_type : "",
      arm_type: values.production_unit === "kollu" ? values.arm_type : "",
      quantity: Number(values.quantity),
    });

    if (ok) {
      await loadStocks();
      resetChassisForm();
      setChassisModalVisible(false);
    }
  };

  const onSubmitArm = async (values: any) => {
    const ok = await UpsertArmStock({
      id: editingArm?.id,
      brand: values.brand,
      location: values.location,
      arm_type: values.arm_type,
      arm_length: values.arm_length,
      meme_distance: values.meme_distance,
      meme_type: values.meme_type,
      quantity: Number(values.quantity),
    });

    if (ok) {
      await loadStocks();
      resetArmForm();
      setArmModalVisible(false);
    }
  };

  const onSubmitTank = async (values: any) => {
    const ok = await UpsertTankStock({
      id: editingTank?.id,
      brand: values.brand,
      caldron_color: values.caldron_color,
      location: values.location,
      tank_size: Number(values.tank_size),
      caldron_type: values.caldron_type,
      quantity: Number(values.quantity),
    });

    if (ok) {
      await loadStocks();
      resetTankForm();
      setTankModalVisible(false);
    }
  };

  const openNewChassisModal = () => {
    resetChassisForm();
    setChassisModalVisible(true);
  };

  const openEditChassisModal = (record: ChassisStock) => {
    setEditingChassis(record);
    chassisForm.setFieldsValue(record);
    setChassisModalVisible(true);
  };

  const openNewArmModal = () => {
    resetArmForm();
    setArmModalVisible(true);
  };

  const openEditArmModal = (record: ArmStock) => {
    setEditingArm(record);
    armForm.setFieldsValue(record);
    setArmModalVisible(true);
  };

  const openNewTankModal = () => {
    resetTankForm();
    setTankModalVisible(true);
  };

  const openEditTankModal = (record: TankStock) => {
    setEditingTank(record);
    tankForm.setFieldsValue(record);
    setTankModalVisible(true);
  };

  const openTransferModal = (stock: TransferStock) => {
    setTransferStock(stock);
    transferForm.resetFields();
    transferForm.setFieldsValue({ quantity: Math.max(Number(stock.record.quantity) || 1, 1) });
    setTransferModalVisible(true);
  };

  const closeTransferModal = () => {
    setTransferModalVisible(false);
    setTransferStock(null);
    transferForm.resetFields();
  };

  const transferSelectedStock = async (values: any) => {
    if (!transferStock) return;

    const payload = {
      source_stock_id: transferStock.record.id,
      target_location: values.target_location,
      quantity: Number(values.quantity),
    };

    const ok =
      transferStock.stockType === "chassis"
        ? await TransferChassisStock(payload)
        : transferStock.stockType === "arm"
        ? await TransferArmStock(payload)
        : await TransferTankStock(payload);

    if (ok) {
      closeTransferModal();
      await loadStocks();
    }
  };

  const deleteChassisStock = async (record: ChassisStock) => {
    const ok = await UpsertChassisStock({
      id: record.id,
      production_unit: record.production_unit,
      type: record.type,
      brand: record.brand,
      location: record.location,
      tank_size: Number(record.tank_size),
      fan_type: record.production_unit === "turbo" ? record.fan_type : "",
      arm_type: record.production_unit === "kollu" ? record.arm_type : "",
      quantity: 0,
    });

    if (ok) {
      if (editingChassis?.id === record.id) {
        resetChassisForm();
        setChassisModalVisible(false);
      }
      await loadStocks();
    }
  };

  const deleteArmStock = async (record: ArmStock) => {
    const ok = await UpsertArmStock({
      id: record.id,
      brand: record.brand,
      location: record.location,
      arm_type: record.arm_type,
      arm_length: record.arm_length,
      meme_distance: record.meme_distance,
      meme_type: record.meme_type,
      quantity: 0,
    });

    if (ok) {
      if (editingArm?.id === record.id) {
        resetArmForm();
        setArmModalVisible(false);
      }
      await loadStocks();
    }
  };

  const deleteTankStock = async (record: TankStock) => {
    const ok = await UpsertTankStock({
      id: record.id,
      brand: record.brand,
      caldron_color: record.caldron_color,
      location: record.location,
      tank_size: Number(record.tank_size),
      caldron_type: record.caldron_type,
      quantity: 0,
    });

    if (ok) {
      if (editingTank?.id === record.id) {
        resetTankForm();
        setTankModalVisible(false);
      }
      await loadStocks();
    }
  };

  const getChassisExportRows = () =>
    chassisStocks.map((record) => ({
      "Üretim Birimi": getProductionUnitText(record.production_unit),
      Tip: getText(textMaps.type, record.type),
      Marka: getText(textMaps.brand, record.brand),
      Konum: getText(textMaps.stock_location, record.location),
      "Depo Boyutu": getText(textMaps.tank_size, record.tank_size),
      "Fan/Kol Tipi":
        record.production_unit === "turbo"
          ? getText(textMaps.cooler_type, record.fan_type)
          : getText(textMaps.arm_type, record.arm_type),
      Adet: Number(record.quantity),
    }));

  const getArmExportRows = () =>
    armStocks.map((record) => ({
      Marka: getText(textMaps.brand, record.brand),
      Konum: getText(textMaps.stock_location, record.location),
      "Kol Tipi": getText(textMaps.arm_type, record.arm_type),
      "Kol Uzunluğu": record.arm_length || "-",
      "Meme Arası": record.meme_distance || "-",
      "Meme Tipi": getText(textMaps.meme_type, record.meme_type),
      Adet: Number(record.quantity),
    }));

  const getTankExportRows = () =>
    tankStocks.map((record) => ({
      Marka: getText(textMaps.brand, record.brand),
      Renk: getText(textMaps.caldron_color, record.caldron_color),
      Konum: getText(textMaps.stock_location, record.location),
      "Depo Boyutu": getText(textMaps.tank_size, record.tank_size),
      "Temizsulu/Susuz": getText(textMaps.caldron_type, record.caldron_type),
      Adet: Number(record.quantity),
    }));

  const getActiveStockCount = () => {
    if (activeStockTab === "arm") return armStocks.length;
    if (activeStockTab === "tank") return tankStocks.length;
    return chassisStocks.length;
  };

  const exportActiveStockTab = () => {
    const exportConfig =
      activeStockTab === "arm"
        ? {
            rows: getArmExportRows(),
            sheetName: "Kol Stok",
            fileName: "kol-stok.xlsx",
          }
        : activeStockTab === "tank"
        ? {
            rows: getTankExportRows(),
            sheetName: "Depo Stok",
            fileName: "depo-stok.xlsx",
          }
        : {
            rows: getChassisExportRows(),
            sheetName: "Şase Stok",
            fileName: "sase-stok.xlsx",
          };

    const worksheet = XLSX.utils.json_to_sheet(exportConfig.rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, exportConfig.sheetName);
    XLSX.writeFile(workbook, exportConfig.fileName);
  };

  const renderAddButton = () => {
    if (activeStockTab === "arm") {
      return (
        <Button type="primary" onClick={openNewArmModal}>
          Kol Stok Ekle
        </Button>
      );
    }

    if (activeStockTab === "tank") {
      return (
        <Button type="primary" onClick={openNewTankModal}>
          Depo Stok Ekle
        </Button>
      );
    }

    return (
      <Button type="primary" onClick={openNewChassisModal}>
        Şase Stok Ekle
      </Button>
    );
  };

  const renderStockActions = () => (
    <Space>
      <Button onClick={exportActiveStockTab} disabled={getActiveStockCount() === 0}>
        Excel'e Aktar
      </Button>
      {renderAddButton()}
    </Space>
  );

  return (
    <div style={{ padding: 16 }}>
      <Spin spinning={isFetching}>
        <Tabs
          activeKey={activeStockTab}
          onChange={setActiveStockTab}
          tabBarExtraContent={renderStockActions()}
        >
          <TabPane tab="Şase Stok" key="chassis">
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={chassisStocks}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 1080 }}
                  columns={[
                    {
                      title: "Üretim Birimi",
                      key: "production_unit",
                      render: (_: any, record: ChassisStock) =>
                        getProductionUnitText(record.production_unit),
                      ...getSearchColumnProps<ChassisStock>("Üretim Birimi", (record) =>
                        getProductionUnitText(record.production_unit)
                      ),
                    },
                    {
                      title: "Tip",
                      key: "type",
                      render: (_: any, record: ChassisStock) => getText(textMaps.type, record.type),
                      ...getSearchColumnProps<ChassisStock>("Tip", (record) =>
                        getText(textMaps.type, record.type)
                      ),
                    },
                    {
                      title: "Marka",
                      key: "brand",
                      render: (_: any, record: ChassisStock) => getText(textMaps.brand, record.brand),
                      ...getSearchColumnProps<ChassisStock>("Marka", (record) =>
                        getText(textMaps.brand, record.brand)
                      ),
                    },
                    {
                      title: "Konum",
                      key: "location",
                      render: (_: any, record: ChassisStock) =>
                        getText(textMaps.stock_location, record.location),
                      ...getSearchColumnProps<ChassisStock>("Konum", (record) =>
                        getText(textMaps.stock_location, record.location)
                      ),
                    },
                    {
                      title: "Depo Boyutu",
                      key: "tank_size",
                      render: (_: any, record: ChassisStock) =>
                        getText(textMaps.tank_size, record.tank_size),
                      ...getNumberColumnProps<ChassisStock>("Depo Boyutu", (record) =>
                        Number(record.tank_size)
                      ),
                    },
                    {
                      title: "Fan/Kol Tipi",
                      key: "stock_type",
                      render: (_: any, record: ChassisStock) =>
                        record.production_unit === "turbo"
                          ? getText(textMaps.cooler_type, record.fan_type)
                          : getText(textMaps.arm_type, record.arm_type),
                      ...getSearchColumnProps<ChassisStock>("Fan/Kol Tipi", (record) =>
                        record.production_unit === "turbo"
                          ? getText(textMaps.cooler_type, record.fan_type)
                          : getText(textMaps.arm_type, record.arm_type)
                      ),
                    },
                    {
                      title: "Adet",
                      dataIndex: "quantity",
                      key: "quantity",
                      width: 90,
                      ...getNumberColumnProps<ChassisStock>("Adet", (record) => Number(record.quantity)),
                    },
                    {
                      title: "İşlem",
                      key: "action",
                      width: 220,
                      render: (_: any, record: ChassisStock) => (
                        <Space>
                          <Button
                            size="small"
                            onClick={() => {
                              openEditChassisModal(record);
                            }}
                          >
                            Düzenle
                          </Button>
                          <Button
                            size="small"
                            onClick={() =>
                              openTransferModal({ stockType: "chassis", record })
                            }
                          >
                            Gönder
                          </Button>
                          <Popconfirm
                            title="Bu stok 0 adete çekilsin mi?"
                            okText="Evet"
                            cancelText="Vazgeç"
                            onConfirm={() => deleteChassisStock(record)}
                          >
                            <Button size="small" danger>
                              Sil
                            </Button>
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]}
                />
          </TabPane>

          <TabPane tab="Kol Stok" key="arm">
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={armStocks}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 1120 }}
                  columns={[
                    {
                      title: "Marka",
                      key: "brand",
                      render: (_: any, record: ArmStock) => getText(textMaps.brand, record.brand),
                      ...getSearchColumnProps<ArmStock>("Marka", (record) =>
                        getText(textMaps.brand, record.brand)
                      ),
                    },
                    {
                      title: "Konum",
                      key: "location",
                      render: (_: any, record: ArmStock) =>
                        getText(textMaps.stock_location, record.location),
                      ...getSearchColumnProps<ArmStock>("Konum", (record) =>
                        getText(textMaps.stock_location, record.location)
                      ),
                    },
                    {
                      title: "Kol Tipi",
                      key: "arm_type",
                      render: (_: any, record: ArmStock) => getText(textMaps.arm_type, record.arm_type),
                      ...getSearchColumnProps<ArmStock>("Kol Tipi", (record) =>
                        getText(textMaps.arm_type, record.arm_type)
                      ),
                    },
                    {
                      title: "Kol Uzunluğu",
                      dataIndex: "arm_length",
                      key: "arm_length",
                      ...getSearchColumnProps<ArmStock>("Kol Uzunluğu", (record) => record.arm_length),
                    },
                    {
                      title: "Meme Arası",
                      dataIndex: "meme_distance",
                      key: "meme_distance",
                      ...getSearchColumnProps<ArmStock>("Meme Arası", (record) => record.meme_distance),
                    },
                    {
                      title: "Meme Tipi",
                      key: "meme_type",
                      render: (_: any, record: ArmStock) => getText(textMaps.meme_type, record.meme_type),
                      ...getSearchColumnProps<ArmStock>("Meme Tipi", (record) =>
                        getText(textMaps.meme_type, record.meme_type)
                      ),
                    },
                    {
                      title: "Adet",
                      dataIndex: "quantity",
                      key: "quantity",
                      width: 90,
                      ...getNumberColumnProps<ArmStock>("Adet", (record) => Number(record.quantity)),
                    },
                    {
                      title: "İşlem",
                      key: "action",
                      width: 220,
                      render: (_: any, record: ArmStock) => (
                        <Space>
                          <Button
                            size="small"
                            onClick={() => {
                              openEditArmModal(record);
                            }}
                          >
                            Düzenle
                          </Button>
                          <Button
                            size="small"
                            onClick={() => openTransferModal({ stockType: "arm", record })}
                          >
                            Gönder
                          </Button>
                          <Popconfirm
                            title="Bu stok 0 adete çekilsin mi?"
                            okText="Evet"
                            cancelText="Vazgeç"
                            onConfirm={() => deleteArmStock(record)}
                          >
                            <Button size="small" danger>
                              Sil
                            </Button>
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]}
                />
          </TabPane>

          <TabPane tab="Depo Stok" key="tank">
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={tankStocks}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 960 }}
                  columns={[
                    {
                      title: "Marka",
                      key: "brand",
                      render: (_: any, record: TankStock) => getText(textMaps.brand, record.brand),
                      ...getSearchColumnProps<TankStock>("Marka", (record) =>
                        getText(textMaps.brand, record.brand)
                      ),
                    },
                    {
                      title: "Renk",
                      key: "caldron_color",
                      render: (_: any, record: TankStock) =>
                        getText(textMaps.caldron_color, record.caldron_color),
                      ...getSearchColumnProps<TankStock>("Renk", (record) =>
                        getText(textMaps.caldron_color, record.caldron_color)
                      ),
                    },
                    {
                      title: "Konum",
                      key: "location",
                      render: (_: any, record: TankStock) =>
                        getText(textMaps.stock_location, record.location),
                      ...getSearchColumnProps<TankStock>("Konum", (record) =>
                        getText(textMaps.stock_location, record.location)
                      ),
                    },
                    {
                      title: "Depo Boyutu",
                      key: "tank_size",
                      render: (_: any, record: TankStock) =>
                        getText(textMaps.tank_size, record.tank_size),
                      ...getNumberColumnProps<TankStock>("Depo Boyutu", (record) =>
                        Number(record.tank_size)
                      ),
                    },
                    {
                      title: "Temizsulu/Susuz",
                      key: "caldron_type",
                      render: (_: any, record: TankStock) =>
                        getText(textMaps.caldron_type, record.caldron_type),
                      ...getSearchColumnProps<TankStock>("Temizsulu/Susuz", (record) =>
                        getText(textMaps.caldron_type, record.caldron_type)
                      ),
                    },
                    {
                      title: "Adet",
                      dataIndex: "quantity",
                      key: "quantity",
                      width: 90,
                      ...getNumberColumnProps<TankStock>("Adet", (record) => Number(record.quantity)),
                    },
                    {
                      title: "İşlem",
                      key: "action",
                      width: 220,
                      render: (_: any, record: TankStock) => (
                        <Space>
                          <Button
                            size="small"
                            onClick={() => {
                              openEditTankModal(record);
                            }}
                          >
                            Düzenle
                          </Button>
                          <Button
                            size="small"
                            onClick={() => openTransferModal({ stockType: "tank", record })}
                          >
                            Gönder
                          </Button>
                          <Popconfirm
                            title="Bu stok 0 adete çekilsin mi?"
                            okText="Evet"
                            cancelText="Vazgeç"
                            onConfirm={() => deleteTankStock(record)}
                          >
                            <Button size="small" danger>
                              Sil
                            </Button>
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]}
                />
          </TabPane>
        </Tabs>

        <Modal
          title={editingChassis ? "Şase Stok Güncelle" : "Şase Stok Ekle"}
          visible={chassisModalVisible}
          okText="Kaydet"
          cancelText="Vazgeç"
          onOk={() => chassisForm.submit()}
          onCancel={() => {
            setChassisModalVisible(false);
            resetChassisForm();
          }}
          destroyOnClose
        >
          <Form layout="vertical" form={chassisForm} onFinish={onSubmitChassis}>
            <Form.Item name="production_unit" label="Üretim Birimi" rules={[{ required: true }]}>
              <Select>
                <Option value="turbo">Turbo</Option>
                <Option value="kollu">Kollu</Option>
              </Select>
            </Form.Item>
            <Form.Item name="type" label="Tip" rules={[{ required: true }]}>
              <Select>
                {(configFile?.type || []).map((item: ConfigItem) => (
                  <Option key={item.type} value={item.type}>
                    {item.text}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="brand" label="Marka" rules={[{ required: true }]}>
              <Select>
                {(configFile?.brand || []).map((item: ConfigItem) => (
                  <Option key={item.type} value={item.type}>
                    {item.text}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="location" label="Konum" rules={[{ required: true }]}>
              <Select>
                {stockLocations.map((item: ConfigItem) => (
                  <Option key={item.type} value={item.type}>
                    {item.text}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="tank_size" label="Depo Boyutu" rules={[{ required: true }]}>
              <Select>
                {(configFile?.tank_size || []).map((item: ConfigItem) => (
                  <Option key={item.type} value={Number(item.type)}>
                    {item.text}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item shouldUpdate noStyle>
              {({ getFieldValue }) =>
                getFieldValue("production_unit") === "turbo" ? (
                  <Form.Item name="fan_type" label="Fan Tipi" rules={[{ required: true }]}>
                    <Select>
                      {(configFile?.cooler_type || []).map((item: ConfigItem) => (
                        <Option key={item.type} value={item.type}>
                          {item.text}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : (
                  <Form.Item name="arm_type" label="Kol Tipi" rules={[{ required: true }]}>
                    <Select>
                      {(configFile?.arm_type || []).map((item: ConfigItem) => (
                        <Option key={item.type} value={item.type}>
                          {item.text}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )
              }
            </Form.Item>
            <Form.Item name="quantity" label="Adet" rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={editingArm ? "Kol Stok Güncelle" : "Kol Stok Ekle"}
          visible={armModalVisible}
          okText="Kaydet"
          cancelText="Vazgeç"
          onOk={() => armForm.submit()}
          onCancel={() => {
            setArmModalVisible(false);
            resetArmForm();
          }}
          destroyOnClose
        >
          <Form layout="vertical" form={armForm} onFinish={onSubmitArm}>
            <Form.Item name="brand" label="Marka" rules={[{ required: true }]}>
              <Select>
                {(configFile?.brand || []).map((item: ConfigItem) => (
                  <Option key={item.type} value={item.type}>
                    {item.text}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="location" label="Konum" rules={[{ required: true }]}>
              <Select>
                {stockLocations.map((item: ConfigItem) => (
                  <Option key={item.type} value={item.type}>
                    {item.text}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="arm_type" label="Kol Tipi" rules={[{ required: true }]}>
              <Select>
                {(configFile?.arm_type || []).map((item: ConfigItem) => (
                  <Option key={item.type} value={item.type}>
                    {item.text}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="arm_length" label="Kol Uzunluğu" rules={[{ required: true }]}>
              <Input maxLength={30} />
            </Form.Item>
            <Form.Item name="meme_distance" label="Meme Arası" rules={[{ required: true }]}>
              <Input maxLength={30} />
            </Form.Item>
            <Form.Item name="meme_type" label="Meme Tipi" rules={[{ required: true }]}>
              <Select>
                {(configFile?.meme_type || []).map((item: ConfigItem) => (
                  <Option key={item.type} value={item.type}>
                    {item.text}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="quantity" label="Adet" rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={editingTank ? "Depo Stok Güncelle" : "Depo Stok Ekle"}
          visible={tankModalVisible}
          okText="Kaydet"
          cancelText="Vazgeç"
          onOk={() => tankForm.submit()}
          onCancel={() => {
            setTankModalVisible(false);
            resetTankForm();
          }}
          destroyOnClose
        >
          <Form layout="vertical" form={tankForm} onFinish={onSubmitTank}>
            <Form.Item name="brand" label="Marka" rules={[{ required: true }]}>
              <Select>
                {(configFile?.brand || []).map((item: ConfigItem) => (
                  <Option key={item.type} value={item.type}>
                    {item.text}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="caldron_color" label="Renk" rules={[{ required: true }]}>
              <Select>
                {caldronColors.map((item: ConfigItem) => (
                  <Option key={item.type} value={item.type}>
                    {item.text}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="location" label="Konum" rules={[{ required: true }]}>
              <Select>
                {stockLocations.map((item: ConfigItem) => (
                  <Option key={item.type} value={item.type}>
                    {item.text}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="tank_size" label="Depo Boyutu" rules={[{ required: true }]}>
              <Select>
                {(configFile?.tank_size || []).map((item: ConfigItem) => (
                  <Option key={item.type} value={Number(item.type)}>
                    {item.text}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="caldron_type" label="Temizsulu/Susuz" rules={[{ required: true }]}>
              <Select>
                {(configFile?.caldron_type || []).map((item: ConfigItem) => (
                  <Option key={item.type} value={item.type}>
                    {item.text}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="quantity" label="Adet" rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Konumlar Arası Gönder"
          visible={transferModalVisible}
          okText="Gönder"
          cancelText="Vazgeç"
          onOk={() => transferForm.submit()}
          onCancel={closeTransferModal}
          destroyOnClose
        >
          {transferStock ? (
            <Form
              layout="vertical"
              form={transferForm}
              onFinish={transferSelectedStock}
            >
              <Form.Item label="Kaynak Konum">
                <Input
                  disabled
                  value={getText(
                    textMaps.stock_location,
                    transferStock.record.location
                  )}
                />
              </Form.Item>
              <Form.Item
                name="target_location"
                label="Hedef Konum"
                rules={[{ required: true }]}
              >
                <Select>
                  {stockLocations
                    .filter((item: ConfigItem) => item.type !== transferStock.record.location)
                    .map((item: ConfigItem) => (
                      <Option key={item.type} value={item.type}>
                        {item.text}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="quantity"
                label="Adet"
                rules={[{ required: true }]}
              >
                <InputNumber
                  min={1}
                  max={Number(transferStock.record.quantity)}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Form>
          ) : null}
        </Modal>
      </Spin>
    </div>
  );
}

export default StockScreen;
